import React from 'react';
import { TrendingUp, BarChart2, AlertCircle, DollarSign } from 'lucide-react';

/**
 * Секция управления инвестициями
 */
const InvestmentsSection = ({ 
  formData,
  handleInputChange,
  errors
}) => {
  // Функция для обработки изменений в инвестициях
  const handleInvestmentsChange = (field, value) => {
    handleInputChange({ 
      target: { 
        name: `investments.${field}`, 
        value,
        type: field === 'enabled' ? 'checkbox' : 'text',
        checked: value
      } 
    });
  };

  return (
    <div className="mb-6">
      <div className="flex items-start mb-4">
        <div className="flex-shrink-0 pt-0.5">
          <input
            type="checkbox"
            id="investmentsEnabled"
            checked={formData.investments.enabled}
            onChange={(e) => handleInvestmentsChange('enabled', e.target.checked)}
            className="form-checkbox"
          />
        </div>
        <div className="ml-3">
          <label htmlFor="investmentsEnabled" className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-purple-500" />
            Инвестиции
          </label>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Включите, если хотите отслеживать долгосрочные инвестиции и их доходность
          </p>
        </div>
      </div>

      {formData.investments.enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 ml-7">
            <div>
              <label htmlFor="portfolioValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Текущая стоимость портфеля (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="portfolioValue"
                  value={formData.investments.portfolioValue}
                  onChange={(e) => handleInvestmentsChange('portfolioValue', e.target.value)}
                  className="form-input w-full pr-6"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
            </div>
            
            <div>
              <label htmlFor="monthlyContribution" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ежемесячный взнос (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="monthlyContribution"
                  value={formData.investments.monthlyContribution}
                  onChange={(e) => handleInvestmentsChange('monthlyContribution', e.target.value)}
                  className={`form-input w-full pr-6 ${errors.investmentsContribution ? 'input-error' : ''}`}
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
              {errors.investmentsContribution && (
                <p className="mt-1 text-xs text-red-500">{errors.investmentsContribution}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="expectedReturn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ожидаемая годовая доходность (%)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="expectedReturn"
                  value={formData.investments.expectedReturn}
                  onChange={(e) => handleInvestmentsChange('expectedReturn', e.target.value)}
                  className={`form-input w-full pr-6 ${errors.investmentsReturn ? 'input-error' : ''}`}
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">%</span>
              </div>
              {errors.investmentsReturn && (
                <p className="mt-1 text-xs text-red-500">{errors.investmentsReturn}</p>
              )}
            </div>
          </div>

          {/* Рекомендации по инвестициям */}
          <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800 ml-7">
            <div className="flex items-start">
              <BarChart2 className="w-5 h-5 text-purple-500 dark:text-purple-400 flex-shrink-0 mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
                  Средняя доходность разных классов активов в России:
                </h4>
                <ul className="text-xs text-purple-600 dark:text-purple-400 space-y-1.5">
                  <li className="flex justify-between items-center">
                    <span>Банковские вклады (2025)</span>
                    <span className="font-medium">5-7% годовых</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>Облигации федерального займа (ОФЗ)</span>
                    <span className="font-medium">7-9% годовых</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>Корпоративные облигации</span>
                    <span className="font-medium">8-11% годовых</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>Российский фондовый рынок (акции)</span>
                    <span className="font-medium">10-15% годовых</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>Недвижимость (с учетом аренды)</span>
                    <span className="font-medium">8-12% годовых</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Предупреждение о долгосрочности */}
          <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 ml-7">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-500 dark:text-yellow-400 flex-shrink-0 mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                  Важно помнить о инвестициях
                </h4>
                <ul className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1 list-disc list-inside">
                  <li>Инвестиции подходят для долгосрочных целей (от 3-5 лет)</li>
                  <li>Все инвестиции сопряжены с определенными рисками</li>
                  <li>Диверсификация снижает риски и делает портфель более устойчивым</li>
                  <li>Рекомендуется начинать инвестировать только после создания резервного фонда</li>
                  <li>В России доступны налоговые льготы через ИИС (Индивидуальный инвестиционный счет)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Пример расчета накоплений */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 ml-7">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
              <DollarSign className="w-4 h-4 mr-1 text-emerald-500" />
              Пример расчета накоплений
            </h4>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <p className="mb-2">
                При ежемесячном взносе {parseInt(formData.investments.monthlyContribution || 0).toLocaleString('ru-RU')} ₽ 
                и средней доходности {formData.investments.expectedReturn}% годовых:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div className="p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Через 1 год</div>
                  <div className="font-semibold text-sm mt-1">
                    {calculateFutureValue(
                      parseFloat(formData.investments.portfolioValue || 0),
                      parseFloat(formData.investments.monthlyContribution || 0),
                      parseFloat(formData.investments.expectedReturn || 0) / 100,
                      1
                    ).toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Через 5 лет</div>
                  <div className="font-semibold text-sm mt-1">
                    {calculateFutureValue(
                      parseFloat(formData.investments.portfolioValue || 0),
                      parseFloat(formData.investments.monthlyContribution || 0),
                      parseFloat(formData.investments.expectedReturn || 0) / 100,
                      5
                    ).toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Через 10 лет</div>
                  <div className="font-semibold text-sm mt-1">
                    {calculateFutureValue(
                      parseFloat(formData.investments.portfolioValue || 0),
                      parseFloat(formData.investments.monthlyContribution || 0),
                      parseFloat(formData.investments.expectedReturn || 0) / 100,
                      10
                    ).toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Через 20 лет</div>
                  <div className="font-semibold text-sm mt-1">
                    {calculateFutureValue(
                      parseFloat(formData.investments.portfolioValue || 0),
                      parseFloat(formData.investments.monthlyContribution || 0),
                      parseFloat(formData.investments.expectedReturn || 0) / 100,
                      20
                    ).toLocaleString('ru-RU')} ₽
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs italic">
                * Расчет приблизительный, не учитывает налоги, инфляцию и другие факторы
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Расчет будущей стоимости инвестиций с регулярными взносами (по формуле сложных процентов)
const calculateFutureValue = (initialAmount, monthlyContribution, annualRate, years) => {
  const monthlyRate = annualRate / 12;
  const months = years * 12;
  
  let futureValue = initialAmount;
  
  for (let i = 0; i < months; i++) {
    futureValue = futureValue * (1 + monthlyRate) + monthlyContribution;
  }
  
  return Math.round(futureValue);
};

export default InvestmentsSection;